import { useMemo } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';

import { API_ENDPOINT, CLIENT_PATH } from '../constants';
import Timetable from './Timetable';
import getSections from '../timetable/getSections';
import { getSectionObjs, meetingsOverlap, parseSectionToCourseBoxes } from '../utils/timetableActions';

import styles from './SharedView.module.css';

function decodeTimetable(value) {
    if (!value) return {};

    return value.split('!').reduce((timetable, entry) => {
        const [courseCode, sectionList] = entry.split(':_');
        if (!courseCode || !sectionList) return timetable;

        const sections = sectionList.split(',').filter(Boolean);
        if (sections.length > 0) timetable[courseCode] = [...new Set(sections)];
        return timetable;
    }, {});
}

function markConflicts(boxes) {
    return boxes.map((box, index) => ({
        ...box,
        hasConflict: boxes.some((candidate, candidateIndex) => (
            index !== candidateIndex
            && meetingsOverlap(box, candidate)
        )),
    }));
}

const SharedView = () => {
    const params = useMemo(() => new URLSearchParams(window.location.search), []);
    const semester = params.get('semester');
    const timetableData = useMemo(() => decodeTimetable(params.get('timetable')), [params]);
    const courseList = useMemo(() => Object.keys(timetableData), [timetableData]);
    const isValidTerm = /^\d{4}$/.test(semester || '');

    const { data, isLoading, isError } = useQuery({
        queryKey: ['sharedData', semester],
        queryFn: () => axios.get(API_ENDPOINT, { params: { term: semester } }).then(response => response.data),
        enabled: isValidTerm,
        retry: 1,
    });

    const { courseBoxes, tbaBoxes, missingSelections } = useMemo(() => {
        const regular = [];
        const tba = [];
        const missing = [];
        if (!data) return { courseBoxes: regular, tbaBoxes: tba, missingSelections: missing };

        courseList.forEach(courseCode => {
            const course = data[courseCode];
            if (!course) {
                missing.push(courseCode);
                return;
            }

            const available = getSections(data, courseCode);
            timetableData[courseCode].forEach(section => {
                const sectionObjects = getSectionObjs(data, courseCode, section);
                if (sectionObjects.length === 0) {
                    missing.push(`${courseCode} ${section}`);
                    return;
                }
                const sectionType = section.match(/^[A-Z]+/i)?.[0];
                const isSingleton = available?.[sectionType]?.length === 1;
                sectionObjects.forEach(sectionObj => {
                    parseSectionToCourseBoxes(courseCode, section, sectionObj, isSingleton, false).forEach(box => {
                        (box.type === 'tba' ? tba : regular).push(box);
                    });
                });
            });
        });

        return { courseBoxes: markConflicts(regular), tbaBoxes: tba, missingSelections: missing };
    }, [courseList, data, timetableData]);

    const termName = useMemo(() => (
        Object.values(data?.terms || {}).find(term => term?.num === semester)?.text || `Term ${semester}`
    ), [data, semester]);

    if (!isValidTerm || courseList.length === 0) {
        return <ErrorCard message="This share link is missing a term or selected courses." />;
    }

    if (isLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingCard}><div className={styles.spinner} /><p>Loading timetable...</p></div>
            </div>
        );
    }

    if (isError) return <ErrorCard message="Could not load course data for this semester." />;
    if (missingSelections.length > 0) {
        return <ErrorCard message={`This share link contains unavailable selections: ${missingSelections.join(', ')}.`} />;
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.title}>Shared Timetable</h1>
                    <span className={styles.term}>{termName}</span>
                </div>
                <a href={CLIENT_PATH || '/'} className={styles.button}>Create Your Own</a>
            </div>
            <div className={styles.content}>
                <aside className={styles.sidebar}>
                    <h3>Courses ({courseList.length})</h3>
                    <div className={styles.courseList}>
                        {courseList.map(code => (
                            <div key={code} className={styles.courseItem}>
                                <div className={styles.courseCode}>{code}</div>
                                <div className={styles.courseName}>{data[code]?.name || 'Unavailable for this term'}</div>
                                <div className={styles.courseSections}>{timetableData[code].join(', ')}</div>
                            </div>
                        ))}
                    </div>
                </aside>
                <div className={styles.timetableWrapper}>
                    <Timetable courseBoxes={courseBoxes} tbaBoxes={tbaBoxes} virtualBoxes={[]} readOnly />
                </div>
            </div>
        </div>
    );
};

function ErrorCard({ message }) {
    return (
        <div className={styles.container}>
            <div className={styles.errorCard}>
                <h2>Invalid Share Link</h2>
                <p>{message}</p>
                <a href={CLIENT_PATH || '/'} className={styles.button}>Create Your Own Timetable</a>
            </div>
        </div>
    );
}

export default SharedView;
